import { default as WJElement, event } from '../wje-element/element.js';
import Popup from '../wje-popup/popup.element.js';

/**
 * `Dropdown` is a custom element that displays a dropdown menu.
 * @summary This element represents a dropdown menu.
 * @documentation https://elements.webjet.sk/components/dropdown
 * @status stable
 * @augments {WJElement}
 * @csspart native - The native part of the dropdown.
 * @slot trigger - The slot for the trigger of the dropdown.
 * @slot - The default slot for the dropdown.
 * // @fires wje-dropdown:open - Event fired when the dropdown is opened.
 * // @fires wje-dropdown:close - Event fired when the dropdown is closed.
 * @tag wje-dropdown
 */
export default class Dropdown extends WJElement {
    /**
     * Creates an instance of Dropdown.
     * @class
     */
    constructor() {
        super();
    }

    /**
     * The placement of the dropdown.
     * @type {{"wje-popup": Popup}}
     */
    dependencies = {
        'wje-popup': Popup,
    };

    /**
     * Sets or removes the 'portaled' attribute on the element.
     * When the value is truthy, the attribute 'portaled' is added to the element.
     * When the value is falsy, the attribute 'portaled' is removed from the element.
     * @param {boolean} value Determines whether to add or remove the 'portaled' attribute.
     */
    set portaled(value) {
        if (value) {
            this.setAttribute('portaled', value);
        } else {
            this.removeAttribute('portaled');
        }
    }

    /**
     * Getter method for the `portaled` property.
     * Checks if the `portaled` attribute is present on the element.
     * @returns {boolean} Returns `true` if the `portaled` attribute exists, otherwise `false`.
     */
    get portaled() {
        return this.getAttribute('portaled') || '';
    }

    /**
     * Checks whether the element has the 'portaled' attribute.
     * @returns {boolean} True if the element has the 'portaled' attribute, otherwise false.
     */
    get isPortaled() {
        return this.hasAttribute('portaled');
    }

    /**
     * Sets the placement of the dropdown.
     * @param value
     */
    set trigger(value) {
        this.setAttribute('trigger', value);
    }

    /**
     * Gets the placement of the dropdown.
     * @returns {string|string}
     */
    get trigger() {
        return this.getAttribute('trigger') || 'click';
    }

    /**
     * Sets the placement of the dropdown.
     * @type {string}
     */
    className = 'Dropdown';

    /**
     * Getter for the CSS stylesheet.
     * @returns {string[]}
     */
    static get observedAttributes() {
        return ['active'];
    }

    /**
     * Callback function to handle other dropdowns being opened. Close the dropdown if it is not the target and collapse is enabled.
     * @param {Event} e The event object.
     */
    otherDropdownOpennedCallback = (e) => {
        if (e.detail.detail.target !== this) {
            this.classList.remove('active');
            this.popup.hide();
        }
    };

    /**
     * Sets up the attributes for the dropdown.
     */
    setupAttributes() {
        this.isShadowRoot = 'open';
    }

    /**
     * Removes the popup element.
     */
    beforeDraw() {
        this.popup?.remove();
        this.popup = null;
    }

    /**
     * Draws the dropdown element and returns the created document fragment.
     * @returns {DocumentFragment}
     */
    draw() {
        let fragment = document.createDocumentFragment();

        this.classList.add('wje-placement', 'wje-' + this.placement || 'wje-start');

        let native = document.createElement('div');
        native.setAttribute('part', 'native');
        native.classList.add('native-dropdown');

        let tooltip = document.createElement('wje-tooltip');
        tooltip.setAttribute('content', this.tooltip);

        let anchorSlot = document.createElement('slot');
        anchorSlot.setAttribute('name', 'trigger');
        anchorSlot.setAttribute('slot', 'anchor');

        let slot = document.createElement('slot');

        let popup = document.createElement('wje-popup');
        popup.setAttribute('placement', this.placement);
        popup.setAttribute('offset', this.offset);
        popup.setAttribute('part', 'popup');

        if(this.isPortaled)
            popup.setAttribute('portal', this.portaled);

        popup.append(anchorSlot, slot);

        // if(this.trigger === "click")
        popup.setAttribute('manual', '');

        native.appendChild(popup);

        fragment.appendChild(native);

        this.popup = popup;
        this.anchorSlot = anchorSlot;

        return fragment;
    }

    /**
     * Adds event listeners for the mouseenter and mouseleave events.
     */
    afterDraw() {
        event.addListener(this, 'wje-popup:hide', null, this.popupHideCallback);

        // Close when any actionable wje-menu-item inside the popup is clicked (works across Shadow DOM via composed path)
        event.addListener(this.popup, 'click', null, this.onMenuItemClick, { capture: true });

        if (this.trigger !== 'click') {
            event.addListener(this, 'mouseenter', null, this.onOpen);
            event.addListener(this, 'mouseleave', null, this.onClose);
        } else {
            event.addListener(this.anchorSlot, 'click', null, this.toggleCallback, { capture: true });
        }

        if (this.hasAttribute('collapsible')) {
            event.addListener(
              Array.from(this.querySelectorAll('wje-menu-item')),
              'click',
              'wje-menu-item:click',
              this.onClose
            );
        }
    }

    /**
     * Adds event listeners for the mouseenter and mouseleave events.
     */
    afterDisconnect() {
        event.removeListener(this, 'mouseenter', null, this.onOpen);
        event.removeListener(this, 'mouseleave', null, this.onClose);
        event.removeListener(this.anchorSlot, 'click', null, this.toggleCallback, { capture: true });
        event.removeListener(this, 'wje-popup:hide', null, this.popupHideCallback);
        event.removeListener(this.popup, 'click', null, this.onMenuItemClick, { capture: true });
        event.removeListener(document, 'wje-menu-item:click', null, this.#onMenuItemCustom, false);
    }

    popupHideCallback = (e) => {
        if (this.classList.contains('active') && e.target.tagName === "WJE-DROPDOWN") {
            this.toggleCallback(e);
        }
    };

    /**
     * Handles delegated clicks from inside the popup and closes the dropdown when a leaf menu item is selected.
     * This works even when the menu is portaled, because we rely on the composed path.
     */
    onMenuItemClick = (e) => {
        // Find nearest wje-menu-item in the composed path
        const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
        const item = path.find((n) => n && n.tagName === 'WJE-MENU-ITEM');
        if (!item) return;

        // Ignore disabled items
        if (item.hasAttribute('disabled') || item.getAttribute('aria-disabled') === 'true') return;

        // If this item contains a nested submenu (wje-menu), it's not a leaf -> don't close yet
        if (typeof item.querySelector === 'function' && item.querySelector('wje-menu')) return;

        // Leaf item selected -> close dropdown (which calls popup.hide(true) inside onClose)
        this.onClose();
    }

    /**
     * @summary Toggles the dropdown element between active and inactive states.
     * Calls the `onOpen` method if the element is currently inactive,
     * and calls the `onClose` method if the element is currently active.
     * @param {Event} e The event object.
     */
    toggleCallback = (e) => {
        if (this.classList.contains('active')) {
            e.stopPropagation();
            this.onClose();
        } else {
            e.stopPropagation();
            this.onOpen(e);
        }
    };

    /**
     * @summary Returns the content to be displayed before showing the dropdown.
     * @returns {any} The content to be displayed.
     */
    beforeShow() {
        return this.content;
    }

    /**
     * This method is called after the dropdown is shown.
     */
    afterShow() {
        // Do nothing
    }

    /**
     * Open the popup element.
     * @param {object} e
     */
    onOpen = (e) => {
        this.classList.add('active');
        Promise.resolve(this.beforeShow(this))
            .then((res) => {
                if (!this.classList.contains('active')) {
                    throw new Error('beforeShow method returned false or not string');
                }

                this.popup.show(true); // Show tooltip

                event.addListener(document, 'wje-menu-item:click', null, this.#onMenuItemCustom, false);

                event.dispatchCustomEvent(this, 'wje-dropdown:open', {
                    bubbles: true,
                    detail: { target: this },
                });

                Promise.resolve(this.afterShow(this));
            })
            .catch((error) => {
                // ak je nejaka chyba tak to len zatvorime
                this.classList.remove('active');
                this.popup.hide(true);
            });
    };

    beforeClose = () => {
        // Do nothing
    };

    afterClose = () => {
        // Do nothing
    };

    onClose = () => {
        this.classList.remove('active');
        Promise.resolve(this.beforeClose(this))
            .then((res) => {
                if (this.classList.contains('active')) {
                    throw new Error('beforeShow method returned false or not string');
                }

                this.popup.hide(true); // Show tooltip

                event.removeListener(document, 'wje-menu-item:click', null, this.#onMenuItemCustom, false);

                event.dispatchCustomEvent(this, 'wje-dropdown:close', {
                    bubbles: true,
                    detail: { target: this },
                });

                Promise.resolve(this.afterClose(this));
            })
            .catch((error) => {
                this.classList.add('active');
                this.popup.show(true);
            });
    }

    #onMenuItemCustom = (e) => {
        const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
        if (!this.popup || !this.popup.floatingEl || !path.includes(this.popup.floatingEl)) return;

        const item = path.find(n => n && n.tagName === 'WJE-MENU-ITEM');
        if (!item) return;
        if (item.hasAttribute('disabled') || item.getAttribute('aria-disabled') === 'true') return;
        if (item.hasAttribute('has-submenu')) return; // parent; nezatváraj

        // Zavri len tento dropdown. NEvolaj stopPropagation na custom evente,
        // aby fungovali aj tvoje app-level listenery (riadok 480).
        this.classList.remove('active');
        this.popup.hide(true);
    }
}
